import React, { useState } from 'react';
import Chart from 'react-apexcharts';
import { CHART_NODATA_CONSTANT, flowChartOptions } from '../Constants';
import { Button, ConfigProvider, Table } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
  SET_SINGLE_METER_VALUE,
  SET_VIEW_SINGLE_METER,
} from '../../../store/features/systemAnalysisSlice';
import dayjs from 'dayjs';
import { clientColumnSorter } from '../../../utils/tableClientSort';

export const FlowAnalysis = ({ showTable = false, analysisData = {} }) => {
  return (
    <div className={`grid grid-cols-2 gap-4`}>
      <div className={`${showTable ? '' : 'col-span-2'}`}>
        <div className={'flex flex-col'}>
          <FlowAnalysisChart analysisData={analysisData} />
          <FlowAnalysisSummaryTable analysisData={analysisData} />
        </div>
      </div>
      {showTable && (
        <div>
          <FlowAnalysisTable analysisData={analysisData} />
        </div>
      )}
    </div>
  );
};

const normalizeFlowChartSeries = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw != null && typeof raw === 'object') return [raw];
  return [];
};

const FlowAnalysisChart = ({ analysisData = {} }) => {
  const [chartError, setChartError] = useState(null);

  const chartSeriesRaw = normalizeFlowChartSeries(analysisData?.chart?.series);
  const chartLabelsRaw = Array.isArray(analysisData?.chart?.labels)
    ? analysisData.chart.labels
    : [];

  const showDataLabel = chartSeriesRaw.some((c) => c?.type?.toLowerCase() === 'scatter');

  const chartSeries = chartSeriesRaw.map((val) => ({
    ...val,
    type: val?.type?.toLowerCase(),
  }));

  const chartLabels = chartLabelsRaw;

  const handleChartError = (error) => {
    console.error('ApexCharts Error:', error);
    setChartError(error.message || 'Chart rendering failed');
  };

  if (chartError) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg bg-gray-100">
        <div className="text-center">
          <p className="font-semibold text-red-500">Chart Error</p>
          <p className="text-sm text-gray-600">{chartError}</p>
        </div>
      </div>
    );
  }

  const canRenderChart = chartSeries.length > 0 && chartLabels.length > 0;

  try {
    return (
      <>
        {canRenderChart ? (
          <Chart
            key={'ConsumptionAnalysisContentChart'}
            options={{
              ...CHART_NODATA_CONSTANT,
              chart: {
                ...flowChartOptions?.options?.chart,
                events: {
                  exception: handleChartError,
                },
              },
              dataLabels: showDataLabel
                ? { enabled: false }
                : flowChartOptions?.options?.dataLabels,
              labels: chartLabels,
            }}
            series={chartSeries}
            type={'line'}
            height={650}
          />
        ) : (
          <div className={'flex h-[650px] flex-col items-center justify-center border text-light-grey-400'}>
            <p className="text-lg">No data</p>
            <p className="mt-2 max-w-md px-4 text-center text-sm">
              No chart data for this meter and date range. Try another date or verify the serial number.
            </p>
          </div>
        )}
      </>
    );
  } catch (error) {
    handleChartError(error);
    return null;
  }
};

const FlowAnalysisSummaryTable = ({ analysisData }) => {
  const { viewSingleMeter } = useSelector((state) => state.SystemAnalysis);
  const tabularDatas = Array.isArray(analysisData?.tabularDatas) ? analysisData.tabularDatas : [];

  const minData =
    tabularDatas.length > 0
      ? tabularDatas.reduce((min, data) => {
          return (data?.min ?? 0) < min ? (data?.min ?? 0) : min;
        }, tabularDatas[0]?.min ?? 0)
      : 0;

  const maxData =
    tabularDatas.length > 0
      ? tabularDatas.reduce((max, data) => {
          return (data?.max ?? 0) > max ? (data?.max ?? 0) : max;
        }, tabularDatas[0]?.max ?? 0)
      : 0;

  const averageData =
    tabularDatas.length > 0
      ? tabularDatas.reduce((average, data) => {
          return average + (data?.average ?? 0);
        }, 0) / tabularDatas.length
      : 0;

  const TABLE_COLUMNS = [
    {
      title: 'Min',
      dataIndex: 'minData',
      align: 'center',
      sorter: clientColumnSorter('minData'),
    },
    {
      title: 'Max',
      dataIndex: 'maxData',
      align: 'center',
      sorter: clientColumnSorter('maxData'),
    },
    {
      title: 'Average',
      dataIndex: 'averageData',
      align: 'center',
      sorter: clientColumnSorter('averageData'),
    },
  ];

  const TABLE_CONFIG = {
    token: {
      borderRadius: 0,
    },
    components: {
      Table: {
        headerBg: '#F5F5F5',
        headerColor: '#2F5E8A',
        headerSortActiveBg: '#F5F5F5',
        headerSortHoverBg: '#F5F5F5',
        fixedHeaderSortActiveBg: '#F5F5F5',
      },
    },
  };

  const DATA = { minData, maxData, averageData };

  return (
    <>
      {viewSingleMeter ? (
        <ConfigProvider theme={TABLE_CONFIG}>
          <Table dataSource={[DATA]} columns={TABLE_COLUMNS} pagination={false}></Table>
        </ConfigProvider>
      ) : (
        <></>
      )}
    </>
  );
};

const FlowAnalysisTable = ({ analysisData = {} }) => {
  const dispatch = useDispatch();
  const { viewSingleMeter } = useSelector((state) => state.SystemAnalysis);
  const tabularDatas = Array.isArray(analysisData?.tabularDatas) ? analysisData.tabularDatas : [];
  const totalData = tabularDatas.length;
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 14,
      pageSize: 3,
      total: totalData,
    },
  });

  return (
    <ConfigProvider
      theme={{
        token: {
          borderRadius: 0,
        },
        components: {
          Table: {
            /* here is your component tokens */
            headerBg: '#F5F5F5',
            headerColor: '#2F5E8A',
            headerSortActiveBg: '#F5F5F5',
            headerSortHoverBg: '#F5F5F5',
            fixedHeaderSortActiveBg: '#F5F5F5',
          },
        },
      }}
    >
      <Table
        className="table-striped-rows"
        dataSource={tabularDatas}
        columns={[
          {
            title: !viewSingleMeter ? 'Serial Number' : 'Date',
            dataIndex: 'data',
            width: '20%',
            sorter: clientColumnSorter('data'),
            render: (text) => {
              return (
                <>
                  {!viewSingleMeter ? (
                    <Button
                      onClick={() => {
                        dispatch(SET_SINGLE_METER_VALUE(text));
                        dispatch(SET_VIEW_SINGLE_METER(true));
                      }}
                    >
                      <p className="text-dark-blue-100">{text}</p>
                    </Button>
                  ) : (
                    <div>{dayjs(text)?.format('DD/MM/YYYY h:mm:ss a') ?? 'NA'}</div>
                  )}
                </>
              );
            },
          },
          {
            title: '<0',
            dataIndex: 'below0',
            sorter: clientColumnSorter('below0'),
          },
          {
            title: 'Q1',
            dataIndex: 'q1',
            sorter: clientColumnSorter('q1'),
          },
          {
            title: 'Q2',
            dataIndex: 'q2',
            sorter: clientColumnSorter('q2'),
          },
          {
            title: 'Q3',
            dataIndex: 'q3',
            sorter: clientColumnSorter('q3'),
          },
          {
            title: 'Q4',
            dataIndex: 'q4',
            sorter: clientColumnSorter('q4'),
          },
          {
            title: '> Q4',
            dataIndex: 'aboveQ4',
            sorter: clientColumnSorter('aboveQ4'),
          },
          {
            title: 'Min',
            dataIndex: 'min',
            sorter: clientColumnSorter('min'),
          },
          {
            title: 'Max',
            dataIndex: 'max',
            sorter: clientColumnSorter('max'),
          },
          {
            title: 'Average',
            dataIndex: 'average',
            sorter: clientColumnSorter('average'),
          },
        ]}
        pagination={tableParams}
        // showSizeChanger={true}
      ></Table>
    </ConfigProvider>
  );
};
